// SuperDesk WebRTC Client for React Web App
import io from 'socket.io-client';

class SuperDeskClient {
  constructor() {
    this.socket = null;
    this.peerConnection = null;
    this.sessionId = null;
    this.isHost = false;
    this.remoteStream = null;
    this.localStream = null;
    this.remoteControlEnabled = false;
    this.serverUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : 'https://superdesk-7m7f.onrender.com';

    // Camera/mic state tracking
    this.remoteCameraTrackId = null;
    this.remoteMicTrackId = null;
    this.micStream = null;
    this.cameraStream = null;
    this.mainStream = null; // Track the main screen share stream

    this.callbacks = {
      onSessionCreated: null,
      onGuestJoined: null,
      onSessionJoined: null,
      onRemoteStream: null,
      onConnectionStateChange: null,
      onConnectionStatus: null,
      onSessionEnded: null,
      onHostStoppedSharing: null,
      onError: null,
      onHostInfo: null,
      onDataChannelOpen: null,
      // Camera/mic callbacks
      onRemoteCameraStream: null,
      onRemoteCameraOff: null,
      onRemoteMicStream: null,
      onRemoteMicOff: null
    };

    this.dataChannel = null;
  }

  async initialize() {
    this.callbacks.onConnectionStatus?.('connecting');
    return new Promise((resolve, reject) => {
      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'], // WebSocket first, polling fallback
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        randomizationFactor: 0.25,
        timeout: 20000,
        upgrade: true, // Allow transport upgrades
        forceNew: false,
        path: '/socket.io/'
      });

      this.socket.on('connect', () => {
        console.log('✅ Connected to SuperDesk server');
        this.callbacks.onConnectionStatus?.('connected');
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.warn('Disconnected from SuperDesk server:', reason);
        this.callbacks.onConnectionStatus?.('disconnected');
      });

      this.socket.io.on('reconnect_attempt', () => {
        this.callbacks.onConnectionStatus?.('reconnecting');
      });

      this.socket.io.on('reconnect', () => {
        this.callbacks.onConnectionStatus?.('connected');
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Connection error:', error);
        this.callbacks.onConnectionStatus?.('reconnecting');
        reject(error);
      });

      this.setupSocketListeners();
    });
  }

  setupSocketListeners() {
    this.socket.on('session-created', (data) => {
      console.log('Session created:', data.sessionId);
      this.sessionId = data.sessionId;
      this.callbacks.onSessionCreated?.(data.sessionId);
    });

    this.socket.on('guest-joined', (data) => {
      console.log('🎉 Guest joined:', data.guestId);
      this.callbacks.onGuestJoined?.(data);
    });

    this.socket.on('session-joined', () => {
      console.log('✅ Successfully joined session');
      this.callbacks.onSessionJoined?.();
    });

    this.socket.on('session-error', (error) => {
      console.error('Session error:', error);
      this.callbacks.onError?.(error);
    });

    this.socket.on('offer', async (data) => {
      await this.handleOffer(data);
    });

    this.socket.on('answer', async (data) => {
      await this.handleAnswer(data);
    });

    this.socket.on('ice-candidate', async (data) => {
      if (data.candidate) {
        await this.handleIceCandidate(data);
      }
    });

    this.socket.on('session-ended', () => {
      console.log('Session ended');
      this.cleanup();
      this.callbacks.onSessionEnded?.();
    });

    // Handle screen-share stop events from different server/client flows
    const handleHostStoppedSharing = () => {
      console.log('Host stopped sharing');
      this.remoteControlEnabled = false;
      this.callbacks.onHostStoppedSharing?.();
    };
    this.socket.on('host-stopped-sharing', handleHostStoppedSharing);
    this.socket.on('screen-share-stopped', handleHostStoppedSharing);

    this.socket.on('session-timeout', () => {
      console.log('Session timed out');
      this.cleanup();
      this.callbacks.onSessionEnded?.();
      this.callbacks.onError?.('Session timed out due to inactivity');
    });

    // Listen for remote camera state from host
    this.socket.on('camera-state', (data) => {
      console.log('📹 WEBAPP: Received camera-state from host:', data);
      if (data.enabled && data.cameraTrackId) {
        this.remoteCameraTrackId = data.cameraTrackId;
        console.log('📹 WEBAPP: Expecting camera track with ID:', data.cameraTrackId);
      } else {
        this.remoteCameraTrackId = null;
        console.log('📹 WEBAPP: Remote camera turned OFF');
        this.callbacks.onRemoteCameraOff?.();
      }
    });

    // Listen for remote mic state from host
    this.socket.on('mic-state', (data) => {
      console.log('🎤 WEBAPP: Received mic-state from host:', data);
      if (data.enabled && data.micTrackId) {
        this.remoteMicTrackId = data.micTrackId;
        console.log('🎤 WEBAPP: Expecting mic track with ID:', data.micTrackId);
      } else {
        this.remoteMicTrackId = null;
        console.log('🎤 WEBAPP: Remote mic turned OFF');
        this.callbacks.onRemoteMicOff?.();
      }
    });
  }

  async createSession() {
    if (!this.socket || !this.socket.connected) {
      await this.initialize();
    }

    this.isHost = true;
    this.socket.emit('create-session', { type: 'web' });
    console.log('Creating session...');
  }

  async joinSession(sessionId) {
    // Normalize session ID to uppercase (server generates uppercase IDs)
    const normalizedSessionId = sessionId ? sessionId.toString().toUpperCase().trim() : '';

    if (!normalizedSessionId || normalizedSessionId.length !== 8) {
      throw new Error('Invalid session ID. Must be 8 characters.');
    }

    if (!this.socket || !this.socket.connected) {
      await this.initialize();
    }

    this.isHost = false;
    this.sessionId = normalizedSessionId;
    this.socket.emit('join-session', normalizedSessionId);

    // Setup peer connection for receiving
    await this.setupPeerConnection();
    console.log('Joining session:', normalizedSessionId);
  }

  async setupPeerConnection() {
    // Fetch ICE servers
    let iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ];

    try {
      const response = await fetch(`${this.serverUrl}/api/webrtc-config`);
      const config = await response.json();
      if (config.iceServers) {
        iceServers = config.iceServers;
      }
    } catch (error) {
      console.warn('Using default ICE servers:', error);
    }

    // Create peer connection with low-latency optimized configuration
    this.peerConnection = new RTCPeerConnection({ 
      iceServers,
      // Reduce ICE candidate gathering for faster connection
      iceCandidatePoolSize: 10,
      // Bundle all media over a single transport (reduces overhead)
      bundlePolicy: 'max-bundle',
      // Use single RTP/RTCP port (reduces NAT traversal complexity)
      rtcpMuxPolicy: 'require'
    });

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.emit('ice-candidate', {
          sessionId: this.sessionId,
          candidate: event.candidate
        });
      }
    };

    this.peerConnection.ontrack = (event) => {
      console.log('📺 WEBAPP: ========== ONTRACK EVENT FIRED ==========');
      console.log('📺 WEBAPP: Track kind:', event.track.kind);
      console.log('📺 WEBAPP: Track id:', event.track.id);
      console.log('📺 WEBAPP: Track label:', event.track.label);
      console.log('📺 WEBAPP: Streams count:', event.streams.length);

      // Handle AUDIO tracks
      if (event.track.kind === 'audio') {
        console.log('🔊 WEBAPP: ===== AUDIO TRACK RECEIVED =====');

        // Check if this is microphone audio (vs system audio from screen capture)
        const isMicAudio = this.remoteMicTrackId === event.track.id;
        // Also check if this audio arrived via renegotiation (after video already exists)
        const arrivedViaRenegotiation = this.mainStream !== null && this.mainStream.getVideoTracks().length > 0;

        console.log('🔊 WEBAPP: Audio source:', isMicAudio ? 'MICROPHONE (ID match)' : arrivedViaRenegotiation ? 'MICROPHONE (renegotiation)' : 'SYSTEM AUDIO');

        if (isMicAudio || arrivedViaRenegotiation) {
          // MIC AUDIO - notify via callback so UI can play it separately
          if (!this.remoteMicTrackId) {
            this.remoteMicTrackId = event.track.id;
            console.log('🎤 WEBAPP: Set remoteMicTrackId from incoming track (race condition fix)');
          }

          const micStream = new MediaStream([event.track]);
          console.log('🎤 WEBAPP: Remote mic audio stream created');
          this.callbacks.onRemoteMicStream?.(micStream);

          // Handle track ended
          event.track.onended = () => {
            console.log('🎤 WEBAPP: Remote mic track ENDED');
            this.remoteMicTrackId = null;
            this.callbacks.onRemoteMicOff?.();
          };

          return; // Mic track handled separately
        }

        // SYSTEM AUDIO - add to mainStream so it plays through video element
        console.log('🔊 WEBAPP: System audio from remote peer');
        if (this.mainStream) {
          console.log('🔊 WEBAPP: Adding system audio track to existing stream');
          this.mainStream.addTrack(event.track);
          // Re-notify with updated stream
          this.callbacks.onRemoteStream?.(this.mainStream);
        }
        return;
      }

      // Handle VIDEO tracks
      if (event.track.kind === 'video') {
        // Check if this is a CAMERA track (different from screen share)
        const isKnownCameraTrack = this.remoteCameraTrackId === event.track.id;
        const hasExistingVideo = this.mainStream && this.mainStream.getVideoTracks().length > 0;
        const isLabeledAsCamera = event.track.label && (
          event.track.label.toLowerCase().includes('camera') ||
          event.track.label.toLowerCase().includes('webcam') ||
          event.track.label.toLowerCase().includes('facetime')
        );

        console.log('📹 WEBAPP: Camera detection check:', {
          trackId: event.track.id,
          remoteCameraTrackId: this.remoteCameraTrackId,
          isKnownCameraTrack,
          hasExistingVideo,
          isLabeledAsCamera,
          trackLabel: event.track.label
        });

        const isCameraTrack = isKnownCameraTrack || hasExistingVideo || isLabeledAsCamera;

        if (isCameraTrack) {
          console.log('📹 WEBAPP: ===== CAMERA TRACK RECEIVED =====');

          // Handle race condition where track arrives before camera-state signal
          if (!this.remoteCameraTrackId) {
            this.remoteCameraTrackId = event.track.id;
            console.log('📹 WEBAPP: Set remoteCameraTrackId from incoming track (race condition fix)');
          }

          // Get or create camera stream
          const cameraStream = event.streams.length > 0 ? event.streams[0] : new MediaStream([event.track]);
          console.log('📹 WEBAPP: Camera stream ready');

          // Notify via callback
          this.callbacks.onRemoteCameraStream?.(cameraStream);

          // Handle track ended
          event.track.onended = () => {
            console.log('📹 WEBAPP: Remote camera track ENDED');
            this.remoteCameraTrackId = null;
            this.callbacks.onRemoteCameraOff?.();
          };

          return; // Camera track handled separately
        }

        // SCREEN SHARE video - this is the main stream
        console.log('📺 WEBAPP: ===== SCREEN SHARE VIDEO RECEIVED =====');
        
        // === ULTRA-LOW LATENCY: Configure receiver for minimal jitter buffer ===
        try {
          const receivers = this.peerConnection.getReceivers();
          const videoReceiver = receivers.find(r => r.track === event.track);
          if (videoReceiver) {
            // Attempt to reduce jitter buffer (experimental - browser dependent)
            const params = videoReceiver.getParameters ? videoReceiver.getParameters() : {};
            console.log('📺 Video receiver parameters:', params);
            // Some browsers support playoutDelayHint for reduced latency
            if (typeof videoReceiver.playoutDelayHint !== 'undefined') {
              videoReceiver.playoutDelayHint = 0; // Minimum delay
              console.log('📺 Set playoutDelayHint = 0 for minimum latency');
            }
          }
        } catch (e) {
          console.log('Receiver optimization not available:', e.message);
        }
      }

      // Get or create the stream for screen share
      let stream;
      if (event.streams.length > 0) {
        stream = event.streams[0];
        console.log('📺 WEBAPP: Using stream from event.streams[0]');
      } else {
        stream = new MediaStream([event.track]);
        console.log('📺 WEBAPP: Created new MediaStream from track');
      }

      // Store as main stream for adding audio tracks later
      this.mainStream = stream;
      this.remoteStream = stream;

      console.log('📺 WEBAPP: Main stream set, notifying UI');
      this.callbacks.onRemoteStream?.(stream);
    };

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection.connectionState;
      console.log('Connection state:', state);
      this.callbacks.onConnectionStateChange?.(state);
    };

    // Handle data channel for receiving handshake and input
    this.peerConnection.ondatachannel = (event) => {
      const channel = event.channel;
      const label = channel.label;
      console.log('📱 Data channel received:', label);
      
      // Handle input/control channels (for remote control)
      if (label === 'input' || label === 'control') {
        this.dataChannel = channel;
        channel.onopen = () => {
          console.log(`📱 ${label} channel opened`);
          this.callbacks.onDataChannelOpen?.();
        };
        channel.onmessage = (msgEvent) => {
          try {
            const msg = JSON.parse(msgEvent.data);
            // Handle handshake messages
            if (msg.type === 'system' && msg.action === 'handshake') {
              console.log('📱 Received host handshake:', msg.channel, msg.data);
              this.hostCapabilities = msg.data?.capabilities || [];
              this.callbacks.onHostInfo?.(msg.data);
            }
          } catch (e) {
            // Not JSON - might be binary or other format
          }
        };
        channel.onclose = () => {
          console.log(`📱 ${label} channel closed`);
          this.dataChannel = null;
        };
      }
      // Handle file transfer channels
      else if (label === 'fileTransfer' || label === 'files' || label === 'file-transfer') {
        channel.binaryType = 'arraybuffer';
        this.fileChannel = channel;
        channel.onopen = () => {
          console.log('📁 File transfer channel opened');
        };
        channel.onclose = () => {
          console.log('📁 File transfer channel closed');
          this.fileChannel = null;
        };
      }
    };
  }

  async handleOffer(data) {
    if (!this.peerConnection) {
      await this.setupPeerConnection();
    }

    console.log('📨 WEBAPP: Received offer, signaling state:', this.peerConnection.signalingState);

    // Handle glare condition - rollback if we have a local offer pending
    if (this.peerConnection.signalingState === 'have-local-offer') {
      console.log('⚠️ WEBAPP: Glare detected, rolling back local offer');
      try {
        await this.peerConnection.setLocalDescription({ type: 'rollback' });
      } catch (e) {
        console.warn('⚠️ WEBAPP: Rollback failed:', e.message);
      }
    }

    // Accept offer if we're in stable state
    if (this.peerConnection.signalingState === 'stable' || this.peerConnection.signalingState === 'have-remote-offer') {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      this.socket.emit('answer', {
        sessionId: this.sessionId,
        targetId: data.from,
        answer
      });
      console.log('📤 WEBAPP: Answer sent');
    } else {
      console.warn('⚠️ WEBAPP: Ignoring offer - wrong state:', this.peerConnection.signalingState);
    }
  }

  async handleAnswer(data) {
    if (this.peerConnection) {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
    }
  }

  async handleIceCandidate(data) {
    if (this.peerConnection) {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
    }
  }

  enableRemoteControl() {
    this.remoteControlEnabled = true;
    this.socket.emit('enable-remote-control', { sessionId: this.sessionId });
  }

  disableRemoteControl() {
    this.remoteControlEnabled = false;
    this.socket.emit('disable-remote-control', { sessionId: this.sessionId });
  }

  sendMouseEvent(type, x, y, button = 0, extra = {}) {
    if (!this.remoteControlEnabled) return;

    // === ULTRA-LOW LATENCY APPROACH ===
    // For MOVE events: Fire-and-forget via data channel (no ack needed)
    // For other events: Include sequence number but don't wait for ack
    
    const data = { action: type, x, y, button, ...extra };

    // Prefer DataChannel for lowest latency (direct P2P)
    // DO NOT WAIT FOR CONFIRMATION - just send immediately!
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        // For move events, send minimal data to reduce serialization overhead
        if (type === 'move') {
          // Ultra-compact format: "M:x,y" with HIGH PRECISION (6 decimals for 1:1 sensitivity)
          // Higher precision = more accurate mouse positioning = better sensitivity match
          this.dataChannel.send(`M:${x.toFixed(6)},${y.toFixed(6)}`);
        } else {
          // Other events use full format
          this.dataChannel.send(JSON.stringify(data));
        }
        // DON'T wait for ACK - newer moves will overwrite old ones anyway
        return;
      } catch (e) {
        // Silently fall through to Socket.IO, don't log (too spammy)
      }
    }

    // Fallback to Socket.IO (lower priority, higher latency)
    this.socket.emit('mouse-event', {
      sessionId: this.sessionId,
      type,
      x,
      y,
      button,
      ...extra
    });
  }

  sendKeyboardEvent(type, key, code, modifiers = {}) {
    if (!this.remoteControlEnabled) return;

    const data = { action: type, key, code, modifiers };

    // Prefer DataChannel for lowest latency (direct P2P)
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        this.dataChannel.send(JSON.stringify(data));
        return;
      } catch (e) {
        // Fall through to Socket.IO
      }
    }

    // Fallback to Socket.IO
    this.socket.emit('keyboard-event', {
      sessionId: this.sessionId,
      type,
      key,
      code,
      modifiers
    });
  }

  endSession() {
    if (this.socket) {
      this.socket.emit('end-session', this.sessionId);
    }
    this.cleanup();
  }

  cleanup() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
    }

    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.mainStream = null;
    this.remoteCameraTrackId = null;
    this.remoteMicTrackId = null;
    this.remoteControlEnabled = false;
  }

  on(event, callback) {
    const callbackName = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`;
    if (this.callbacks.hasOwnProperty(callbackName)) {
      this.callbacks[callbackName] = callback;
    }
  }
}

export default SuperDeskClient;
